import { useEffect, useMemo, useState } from 'react';
import type { AltYDiscoveryDoc } from '../types/global';
import { MarkdownText } from './MarkdownText';

type DiscoveryCanvasProps = {
  doc: AltYDiscoveryDoc;
  // Close affordance. On desktop this flips the right pane back to the OneSheet
  // iframe; on the mobile overlay it drops back to the conversation. Omitted →
  // no close button.
  onClose?: () => void;
};

type TabKey = 'rephrase' | 'goalGaps' | 'questions' | 'userStory' | 'design';

// The rephrase plus the four pieces, each its own tab. Order is the order the
// agent produces them: the rephrase is shown first (it lands immediately), then
// the gaps, questions, user story, and design ideas.
const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'rephrase', label: 'Rephrase', icon: 'fa-quote-left' },
  { key: 'goalGaps', label: 'Goal & gaps', icon: 'fa-puzzle-piece' },
  { key: 'questions', label: 'Questions', icon: 'fa-question-circle' },
  { key: 'userStory', label: 'User story', icon: 'fa-list-ol' },
  { key: 'design', label: 'Design', icon: 'fa-palette' },
];

// Normalise a tab's value to markdown text. `questions` may arrive as an array
// (the preferred shape) — render it as a numbered list so it reads as a
// checklist; everything else is already markdown.
function tabMarkdown(doc: AltYDiscoveryDoc, key: TabKey): string {
  if (key === 'questions') {
    const q = doc.questions;
    if (Array.isArray(q)) {
      return q
        .map((item) => String(item ?? '').trim())
        .filter(Boolean)
        .map((item, index) => `${index + 1}. ${item}`)
        .join('\n');
    }
    return (q || '').trim();
  }
  return (doc[key] || '').trim();
}

// The Goal Discovery brief — the right-pane (desktop) / full-screen overlay
// (mobile) view. Tabbed: a rephrase of the goal plus four pieces (filled-in
// gaps, key questions, a step-by-step user story, and design ideas). Read-only;
// the user iterates by chatting with the agent, which re-emits the whole brief.
export function DiscoveryCanvas({ doc, onClose }: DiscoveryCanvasProps) {
  // Which tabs actually have content this turn — used to pick a sensible
  // default tab and to mark empty ones.
  const filled = useMemo(() => {
    const set = new Set<TabKey>();
    for (const tab of TABS) if (tabMarkdown(doc, tab.key)) set.add(tab.key);
    return set;
  }, [doc]);

  const [active, setActive] = useState<TabKey>('rephrase');
  // When a fresh brief arrives, snap to the first tab that has content (the
  // rephrase, normally) so the user never opens onto an empty tab.
  useEffect(() => {
    if (filled.has(active)) return;
    const first = TABS.find((tab) => filled.has(tab.key));
    if (first) setActive(first.key);
  }, [filled, active]);

  const body = tabMarkdown(doc, active);

  return (
    <div className="alty-disc">
      <header className="alty-disc-head">
        <span className="alty-disc-title">
          <i className="fas fa-bullseye" aria-hidden="true" />
          <span>{doc.title?.trim() || 'Goal Discovery'}</span>
          {doc.simple ? <span className="alty-disc-simple">simple</span> : null}
        </span>
        {onClose ? (
          <button type="button" className="alty-icon-button" title="Back" aria-label="Back" onClick={onClose}>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        ) : null}
      </header>
      <div className="alty-disc-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active === tab.key}
            className={[
              'alty-disc-tab',
              active === tab.key ? 'active' : '',
              filled.has(tab.key) ? '' : 'empty',
            ].filter(Boolean).join(' ')}
            onClick={() => setActive(tab.key)}
            title={tab.label}
          >
            <i className={`fas ${tab.icon}`} aria-hidden="true" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
      <div className="alty-disc-body" role="tabpanel">
        {body ? (
          <MarkdownText text={body} />
        ) : (
          <div className="alty-disc-empty">
            <i className="fas fa-hourglass-half" aria-hidden="true" />
            <p>
              {doc.simple
                ? 'Not needed for this simple request.'
                : 'Nothing here yet — the agent is still working on this piece.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
